// Lens Word Journal (Parent Dashboard): every word a child tapped with the
// Magic Lens, newest first, with lookup counts, saved meanings and the
// ES/FR/DE translations — a window into what sparked their curiosity.
import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api } from "../../lib/api";

const CHIP = "rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-bold";

export const LensJournal = () => {
  const [children, setChildren] = useState([]);
  const [active, setActive] = useState(null);
  const [words, setWords] = useState(null);

  useEffect(() => {
    api.get("/children").then(({ data }) => {
      setChildren(data || []);
      if (data?.length) setActive(data[0].child_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!active) return;
    setWords(null);
    api.get(`/story/lens/journal/${active}`)
      .then(({ data }) => setWords(data.words))
      .catch(() => setWords([]));
  }, [active]);

  return (
    <div className="space-y-4" data-testid="lens-journal">
      <div className="flex flex-wrap items-center gap-2">
        {children.map((c) => (
          <button key={c.child_id} type="button" onClick={() => setActive(c.child_id)}
            className={`rounded-full px-4 py-1.5 text-sm font-extrabold border-2 transition-colors ${
              active === c.child_id ? "bg-secondary text-white border-secondary" : "bg-card border-border"}`}
            data-testid={`lens-journal-child-${c.child_id}`}>
            {c.display_name}
          </button>
        ))}
      </div>
      {words === null && <p className="text-sm text-muted-foreground p-4">Loading word journal...</p>}
      {words?.length === 0 && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center" data-testid="lens-journal-empty">
          <Search className="h-10 w-10 mx-auto text-muted-foreground mb-3" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            No lens lookups yet — words your child taps in stories with the Magic Lens will collect here.
          </p>
        </div>
      )}
      {words?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {words.map((w) => (
            <div key={w.word} className="rounded-2xl border border-border bg-card p-4 space-y-1.5"
              data-testid={`lens-journal-word-${w.word}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-base font-extrabold font-display capitalize">{w.word}</p>
                <span className={CHIP}>
                  {w.count > 1 ? `looked up ${w.count} times` : "looked up once"}
                </span>
              </div>
              <p className="text-sm text-foreground">{w.meaning}</p>
              <div className="flex flex-wrap gap-1.5">
                {["es"].map((l) => w.translations?.[l] && (
                  <span key={l} className={CHIP}>
                    {l.toUpperCase()}: <span className="capitalize">{w.translations[l]}</span>
                  </span>
                ))}
              </div>
              {w.last_at && (
                <p className="text-[11px] text-muted-foreground">
                  Last looked up {new Date(w.last_at).toLocaleDateString()}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
